import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { useWindowSize } from '@brands/hooks';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal, { Styles } from 'react-modal';

import styles from './styles.module.scss';
import { normalizeUrl } from '../EducationSection';

export type EducationForm = {
  title: string;
  url: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (data: EducationForm) => Promise<void>;
  initialData: EducationForm | null;
};

const AddEducationModal = ({ open, onClose, onSave, initialData }: Props) => {
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const {
    register,
    formState: { errors },
    reset,
    clearErrors,
    setValue,
    getValues,
    trigger,
  } = useForm<EducationForm>({
    defaultValues: {
      title: '',
      url: '',
    },
  });

  useEffect(() => {
    if (initialData) {
      setValue('title', initialData.title || '');
      setValue('url', initialData.url || '');
    } else {
      reset();
    }
  }, [initialData, open, reset, setValue]);

  const handleSave = async (): Promise<void> => {
    const isValid = await trigger();

    if (!isValid) {
      displayErrorDetails('Please fill all fields correctly');
      return;
    }

    const data = getValues();

    if (!data.url.trim() || !data.title.trim()) {
      displayErrorDetails('Please fill all fields before saving');
      return;
    }

    try {
      const normalizedUrl = normalizeUrl(data.url);

      await onSave({
        title: data.title.trim(),
        url: normalizedUrl,
      });

      reset();
      onClose();
    } catch (error) {
      displayErrorDetails(`Failed to save education: ${error}`);
    }
  };

  const modalStyles: Styles = isMobile
    ? {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '100vw',
          height: '100vh',
          borderRadius: 0,
          padding: '90px 16px 32px 16px',
        },
        overlay: {
          ...(customStylesDashboard.overlay || {}),
          overflowY: 'auto',
        },
      }
    : {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '420px',
          padding: '30px 32px 32px',
        },
      };

  const isEditMode = Boolean(initialData);

  return (
    <Modal isOpen={open} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <div className={styles.educationModal}>
        {/* Header */}
        <div className={styles.bodyHead}>
          <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} />
        </div>

        <div className={styles.formContainer}>
          <span className={styles.bodyHeader}>{isEditMode ? 'Edit Education' : 'Add Education'}</span>

          <div className={styles.bodySub}>
            <a href="https://dentistry.one/resources/" target="_blank" rel="noreferrer" className={styles.resourceLink}>
              <OpenInNewIcon />
              Open resource center
            </a>

            <TextField
              {...register('url', {
                required: 'URL is required',
                validate: (value) => {
                  const trimmed = value.trim();
                  if (!trimmed) return 'URL is required';

                  const normalized = normalizeUrl(trimmed);

                  try {
                    new URL(normalized);
                    return true;
                  } catch {
                    return 'Please enter a valid URL';
                  }
                },
              })}
              label="URL"
              name="url"
              errors={errors.url ? errors : {}}
              placeholder="https://"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                clearErrors('url');
                setValue('url', e.target.value, { shouldValidate: true });
              }}
            />

            <TextField
              {...register('title', { required: 'Title is required' })}
              label="Title"
              name="title"
              errors={errors.title ? errors : {}}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                clearErrors('title');
                setValue('title', e.target.value, { shouldValidate: true });
              }}
            />
          </div>

          <div className={styles.bodyFooter}>
            <Button variant="contained" className={styles.containedBtn} onClick={handleSave}>
              {isEditMode ? 'Save Changes' : 'Add Education'}
            </Button>

            <Button variant="text" className={styles.textBtn} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddEducationModal;
